/*
 * Copyright (c) 2024 CalAmp Corp. All Rights Reserved
 */
import React, {useCallback} from 'react';
import {makeStyles} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import {getStatusColors} from '@ion/dls/src/hooks/useStatusBadgeColors';
import ConditionalRender from '@ion/dls/src/render/ConditionalRender';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';

const MINIMUM_PERCENTAGE = 15;

export type MiniBarChartProps = {
  completed: number,
  failed: number,
  inProgress: number,
  cancelled: number,
  total: number
};

type MiniBarChartStyles = {
  remainingPercent: boolean
};

export const useStyles = makeStyles((theme: DLSTheme) => {
  return {
    bar : {
      display               : 'flex',
      flexFlow              : 'row nowrap',
      gap                   : '2px',
      minHeight             : '24px',
      maxHeight             : '24px',
      padding               : '2px',
      width                 : '100%',
      backgroundColor       : dlsColor('white'),
      boxSizing             : 'border-box',
      '& > div:first-child' : {
        borderTopLeftRadius    : '2.5px',
        borderBottomLeftRadius : '2.5px'
      },
      '& > div:last-child' : {
        borderTopRightRadius    : ({remainingPercent}: MiniBarChartStyles) => !remainingPercent ? '2.5px' : '0',
        borderBottomRightRadius : ({remainingPercent}: MiniBarChartStyles) => !remainingPercent ? '2.5px' : '0'
      },
      '& > div:nth-last-child(2)' : {
        borderTopRightRadius    : ({remainingPercent}: MiniBarChartStyles) => remainingPercent ? '2.5px' : '0',
        borderBottomRightRadius : ({remainingPercent}: MiniBarChartStyles) => remainingPercent ? '2.5px' : '0'
      },
      outline      : `1px solid ${dlsColor('grey6')}50`,
      borderRadius : '4px'
    },
    percentage : {
      height         : '100%',
      display        : 'flex',
      alignItems     : 'center',
      justifyContent : 'center',
      ...dlsAlt('typography', 'label1'),
      fontSize       : '12px',
      cursor         : 'default'
    }
  };
});

const getTooltipTitle = (status: 'completed' | 'failed' | 'pending', items: number): string => (
  `${items} ${translate(`jobsearchengine:jobDetails.${items > 1 ? 'devices' : 'device'}`)} ${translate(`jobsearchengine:jobStatus.${status}`)}`
);

/**
 * @MiniBarChart
 * 
 * This component renders a mini bar chart representing the completion status of a Task's Devices.
 * It visually indicates the percentages of completed, failed, and in-progress Devices out of a total
 * number of Devices.
 * 
 * The component uses conditional rendering to ensure that bars are only shown if their respective 
 * percentages are greater than zero. The background colors of the bars are set dynamically based on 
 * the Device status they represent. If the percentage is below a certain threshold (defined by 
 * MINIMUM_PERCENTAGE), the percentage text will not be displayed inside the bar.
 * 
 * ```typescript
 * import MiniBarChart from './MiniBarChart';
 * 
 * ...
 * return (
 *   <IonGrid
 *   ...
 *    columnComponents={
 *      barChartRenderer : (data) => (
 *        <MiniBarChart completed={data.value.completed} // 50
 *          failed={data.value.failed} // 30
 *          inProgress={data.value.inProgress} // 10
 *          total={data.value.deviceCount} />
 *        )
 *      }
 *   ...
 * />
 * );
 * 
 * ```
 * 
 * This would render a bar chart with three segments: 
 * - A green segment representing 50% (completed)
 * - A red segment representing 30% (failed)
 * - A grey segment representing 10% (in-progress)
 * 
 * The remaining space in the bar (if any) is displayed as white.
 * 
 * @param completed - The number of completed Devices
 * @param failed - The number of failed Devices
 * @param inProgress - The number of in-progress Devices
 * @param total - The total number of Devices
 * 
 * @returns A mini bar chart component for displaying the completion status of a Task's Devices.
 */
export const MiniBarChart = ({
  completed,
  failed,
  inProgress,
  cancelled,
  total
}: MiniBarChartProps) => {
  const calculatePercentage = useCallback((value: number) => {
    const activeTotal = total - cancelled;
    return parseFloat((activeTotal > 0 ? (value / activeTotal) * 100 : 0).toFixed(1));
  }, [total, cancelled]);

  const completedPercent  = calculatePercentage(completed);
  const failedPercent     = calculatePercentage(failed);
  const inProgressPercent = calculatePercentage(inProgress);
  const remainingPercent  = 100 - (completedPercent + failedPercent + inProgressPercent);
  const classes           = useStyles({remainingPercent: remainingPercent > 0});

  const {valid, invalid, other} = getStatusColors();

  const barStyle = useCallback((color: string, width: number) => ({
    backgroundColor : color,
    width           : `${width}%`,
    height          : '20.5px'
  }), []);

  return (
    <div className={classes.bar}>
      <ConditionalRender condition={completedPercent > 0}>
        <Tooltip arrow placement='top' title={getTooltipTitle('completed', completed)}>
          <div style={barStyle(valid.color, completedPercent)}>
            <ConditionalRender condition={completedPercent > MINIMUM_PERCENTAGE}>
              <span className={classes.percentage} style={{color: valid.textColor}}>{completedPercent}%</span>
            </ConditionalRender>
          </div>
        </Tooltip>
      </ConditionalRender>

      <ConditionalRender condition={failedPercent > 0}>
        <Tooltip arrow placement='top' title={getTooltipTitle('failed', failed)}>
          <div style={barStyle(invalid.color, failedPercent)}>
            <ConditionalRender condition={failedPercent > MINIMUM_PERCENTAGE}>
              <span className={classes.percentage} style={{color: invalid.textColor}}>{failedPercent}%</span>
            </ConditionalRender>
          </div>
        </Tooltip>
      </ConditionalRender>

      <ConditionalRender condition={inProgressPercent > 0}>
        <Tooltip arrow placement='top' title={getTooltipTitle('pending', inProgress)}>
          <div style={barStyle(other.color, inProgressPercent)}>
            <ConditionalRender condition={inProgressPercent > MINIMUM_PERCENTAGE}>
              <span className={classes.percentage} style={{color: other.textColor}}>{inProgressPercent}%</span>
            </ConditionalRender>
          </div>
        </Tooltip>
      </ConditionalRender>

      <ConditionalRender condition={remainingPercent > 0}>
        <div style={barStyle(dlsColor('white'), remainingPercent)} />
      </ConditionalRender>
    </div>
  );
};

export default MiniBarChart;
